import React from 'react'
import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux'
import { CircularProgress } from '@mui/material'

import MediaViewerModal from '../../../ChatModals/MediaViewerModal'

import { shouldScrollToBottom } from './helper'
import useMediaCache from '@SonarCX/hooks/useMediaCache'
import { selectSentMedia, clearId } from '../../../../../slices/sentMedia'
import { useChatScroll } from '../../../../../providers/ChatScrollProvider'
import useStyles from './styles'

const Image = ({
  media,
  observer,
  className,
  overlay,
  number,
  setLoading,
  setResetChunks
}) => {
  const dispatch = useAppDispatch()
  const { getMedia, status, media: urls } = useMediaCache()
  const { ids } = useAppSelector(selectSentMedia)
  const { quickScrollToBottom } = useChatScroll()

  const [scrolled, setScrolled] = React.useState(false)
  const [src, setSrc] = React.useState(null)
  const [modalOpen, setModalOpen] = React.useState(false)
  const ref = React.useRef(null)
  const classes = useStyles()

  const { id } = media

  // Starts fetching media only if it's in viewport of chat window
  React.useEffect(() => {
    if (id) {
      getMedia(id)
    }
  }, [id, getMedia])

  // Set media
  React.useEffect(() => {
    if (!id) {
      return
    }

    if (status[id] === 'DONE') {
      setSrc(urls[id])
    }
  }, [id, status, urls, setLoading])

  // Should update scroll after render
  React.useLayoutEffect(() => {
    if (src && !scrolled && observer?.boundingClientRect.y > 240) {
      const timer = setTimeout(() => {
        setScrolled(true)
      }, 500)

      return () => {
        clearTimeout(timer)
      }
    }
  }, [src, scrolled, observer])

  // Scroll once when this image is loaded
  React.useLayoutEffect(() => {
    if (ids.includes(id) && src) {
      dispatch(clearId(id))
    }
  }, [ids, id, src, dispatch])

  React.useLayoutEffect(() => {
    if (
      ref.current &&
      ref.current.complete &&
      !scrolled &&
      shouldScrollToBottom()
    ) {
      quickScrollToBottom()
      setScrolled(true)
    }
    // this prevents the scrollbug
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrolled, shouldScrollToBottom])

  if (!src) {
    return (
      <div className={className}>
        <CircularProgress style={{ position: 'relative' }} />
      </div>
    )
  }

  return (
    <div className={className}>
      {overlay && (
        <div className={classes.text} onClick={() => setResetChunks(true)}>
          + {number} more
        </div>
      )}

      <img
        ref={ref}
        src={src}
        alt='<Media>'
        onClick={() => setModalOpen(true)}
        className={clsx(
          classes.root,
          className && classes.maxHeight,
          overlay && classes.opaque
        )}
      />

      {!overlay && modalOpen && (
        <MediaViewerModal
          file={modalOpen ? media : null}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  )
}

export default Image
